import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { Subject, take, takeUntil } from 'rxjs';

// MODULES

import { EFulfillmentOrderAction } from '@app/core/_constant/fulfillment-orders.constants';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { FulfillmentOrderApiService } from 'v2/modules/shared/services/api/fulfillment-order-api.service';
import { LoadingDialogUtilService } from 'v2/modules/shared/services/util/loading-dialog-util.service';
import { UiFulfillmentActionConfirmationDialogComponent } from 'v2/modules/ui/components/ui-fulfillment-action-confirmation-dialog/ui-fulfillment-action-confirmation-dialog.component';
import { UiModule } from 'v2/modules/ui/ui.module';

// COMPONENTS

// SERVICES

// CONFIGS

import { FulfillmentOrdersGridActionService } from '../../services/fulfillment-orders-grid-action.service';
import { DialogConfig } from '../../../../../shared';
import {
    InvoiceBulkCreateDialogComponent,
} from '../../../../../invoice/components/invoice-bulk-create-dialog/components/invoice-bulk-create-dialog.component';
import {
    IBulkCreateInvoiceActionConfig
} from '../../../../../invoice/components/invoice-bulk-create-dialog/constants/invoice-bulk-create-dialog.constants';
import {
    FulfillmentOrdersGridBulkCreateInvoiceConfig
} from '../../configs/fulfillment-orders-grid-bulk-create-invoice.config';
import { FeatureModule } from '../../../../../../shared/feature/feature.module';

@Component({
  selector: 'lum-fulfillment-orders-grid-actions',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    UiModule,
    MatDividerModule,
    FeatureModule,
  ],
  templateUrl: './fulfillment-orders-grid-actions.component.html',
  styleUrls: ['./fulfillment-orders-grid-actions.component.scss'],
})
export class FulfillmentOrdersGridActionsComponent implements OnInit {
  private onDestroy$ = new Subject();
  ftPushUnPushToFulfilllmentChannel :boolean = false;
  ftChannelTypes: boolean = false;

  @Output()
  clearGridState = new EventEmitter<void>();

  @Output()
  refetchGridData = new EventEmitter<void>();

  @Input()
  selectedRowIds :number[] = [];

  @Input()
  selectedRowCount :number = 0;

  @Input()
  visibleRows :any[] = [];

  @Input()
  selectedfulfillmentConfirmationValues :any[] = [];

  @Input()
  requestParams :{ [param :string] :string | string[] } = {};

  @Input()
  salesOrderId :number = 0;

  @Input()
  selectedRows: any[] = [];

  @Output()
  createInvoicesEvent = new EventEmitter<boolean>();

  constructor(
    private dialog :MatDialog,
    private snackBar :MatSnackBar,
    private loadingDialogService :LoadingDialogUtilService,
    private fulfillmentOrderApiService :FulfillmentOrderApiService,
    private fulfillmentOrdersGridActionService :FulfillmentOrdersGridActionService,
    private featureToggleService :FeatureToggleService,
  ) {}

  ngOnInit() :void {
    this.ftPushUnPushToFulfilllmentChannel = this.featureToggleService.isActive(
      'red_DV-3334_push_unpush_fulfillment_channel',
    );
  }

  onClearGridState() :void {
    this.clearGridState.emit();
  }

  createFulfillmentOrder() :void {
    this.fulfillmentOrdersGridActionService.createFulfillmentOrder(
      this.salesOrderId,
    );
  }

  onPushToFulfillmentChannel() :void {
    // Find all already pushed items
    const alreadyPushedItems = this.selectedfulfillmentConfirmationValues.filter(
      (item) => item.isPushed || item.sourceReferenceIdentifier,
    );

    if (alreadyPushedItems.length > 0) {
      const pushedOrderIds = alreadyPushedItems
        .map((item) => item.orderId)
        .join(', ');
      this.snackBar.open(
        `Cannot push. The following fulfillment order id(s) are already pushed: ${pushedOrderIds}`,
        'Warning',
        { duration: SnackBarConfig.DEFAULT.duration },
      );

      return;
    }

    this.dialog
      .open(UiFulfillmentActionConfirmationDialogComponent, {
        data: {
          title: 'Confirm Push to Fulfillment Channel',
          message:
            'Are you sure you want to push the selected orders to the respective channel?',
          fulfillmentConfirmationValues: this.selectedfulfillmentConfirmationValues,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Pushing fulfillment...');

          this.fulfillmentOrderApiService
            .processFulfillmentOrderChannelAction({
              fulfillmentOrderIds: this.selectedRowIds,
              action: EFulfillmentOrderAction.PUSH_TO_FULFILLMENT_CHANNEL,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response :any) => {
                this.loadingDialogService.close();

                const successIds = response.data?.successFulfillmentOrderIds || [];
                const failed = response.data?.failed || [];

                const successCount = successIds.length;
                const failCount = failed.length;

                if (successCount > 0) {
                  this.snackBar.open(
                    `${successCount} fulfillment order(s) pushed successfully.`,
                    'Success',
                    { duration: SnackBarConfig.DEFAULT.duration },
                  );
                }

                if (failCount > 0) {
                  const failedMessagesArr = failed.map(function (fail) {
                    return `ID ${fail.fulfillmentOrderId}: ${fail.message}`;
                  });

                  const failedMessages = failedMessagesArr.join(', ');
                  this.snackBar.open(
                    `${failCount} failed to push: ${failedMessages}`,
                    'Fail',
                    { duration: SnackBarConfig.DEFAULT.duration },
                  );
                }

                this.onRefretchGridData();
              },
              error: (response :any) => {
                this.loadingDialogService.close();
                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
            });
        }
      });
  }

  onRemoveFromFulfillmentChannel() :void {
    // Find all not pushed items
    const notPushedItems = this.selectedfulfillmentConfirmationValues.filter(
      (item) => !item.isPushed,
    );

    if (notPushedItems.length > 0) {
      const notPushedOrderIds = notPushedItems
        .map((item) => item.orderId)
        .join(', ');
      this.snackBar.open(
        `Cannot remove to fullfillment channel. The following fulfillment order id(s) are not pushed: ${notPushedOrderIds}`,
        'Warning',
        { duration: SnackBarConfig.DEFAULT.duration },
      );

      return;
    }

    this.dialog
      .open(UiFulfillmentActionConfirmationDialogComponent, {
        data: {
          title: 'Confirm Unpush from WMS',
          message:
            'Are you sure you want to remove the selected orders from the respective channel?',
          fulfillmentConfirmationValues: this.selectedfulfillmentConfirmationValues,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Removing fulfillment...');
          this.fulfillmentOrderApiService
            .processFulfillmentOrderChannelAction({
              fulfillmentOrderIds: this.selectedRowIds,
              action: EFulfillmentOrderAction.REMOVE_FROM_FULFILLMENT_CHANNEL,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response :any) => {
                this.loadingDialogService.close();

                const successIds = response.data?.successFulfillmentOrderIds || [];
                const failed = response.data?.failed || [];

                const successCount = successIds.length;
                const failCount = failed.length;

                if (successCount > 0) {
                  this.snackBar.open(
                    `${successCount} fulfillment order(s) removed successfully.`,
                    'Success',
                    { duration: SnackBarConfig.DEFAULT.duration },
                  );
                }

                if (failCount > 0) {
                  const failedMessagesArr = failed.map(function (fail) {
                    return `ID ${fail.fulfillmentOrderId}: ${fail.message}`;
                  });

                  const failedMessages = failedMessagesArr.join(', ');
                  this.snackBar.open(
                    `${failCount} failed to remove: ${failedMessages}`,
                    'Fail',
                    { duration: SnackBarConfig.DEFAULT.duration },
                  );
                }

                this.onRefretchGridData();
              },
              error: (response :any) => {
                this.loadingDialogService.close();
                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              },
            });
        }
      });
  }

  private onRefretchGridData() :void {
    this.refetchGridData.emit();
  }

  onBatchCreateInvoices() :void {
    if (this.selectedRowIds.length === 0) {
      this.snackBar.open('No orders selected for invoice creation.', 'Close', {
        duration: SnackBarConfig.DEFAULT.duration,
      });

      return;
    }

    this.selectedRows.forEach(row => {
      row.invoicingErrors = row.invoicingErrors ?? [];
      const msg = 'Fulfillment order already invoiced.';
      if (row.clientInvoices?.length && !row.invoicingErrors.includes(msg)) {
        row.invoicingErrors.push(msg);
      }
    });

    this.dialog
      .open(InvoiceBulkCreateDialogComponent, {
        ...DialogConfig.LARGE,
        panelClass: 'my-dialog',
        data: {
          items: this.selectedRows,
          idKey: 'id',
          errorKey: 'invoicingErrors',
          columns: FulfillmentOrdersGridBulkCreateInvoiceConfig.getTableColumnConfig(),
          title: 'Create Fulfillment Order Invoices',
          actionLabel: 'Create Invoice',
          actionFn: (id) =>
            this.fulfillmentOrderApiService.createInvoice(id),
        } as IBulkCreateInvoiceActionConfig<any>,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((updated: boolean) => {
        if (updated) {
          this.onRefretchGridData();
          this.createInvoicesEvent.emit(updated);
        }
      });

    return;
  }
}
